import { Loader2, Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { localize } from '../i18n/script';
import { picksApi, type MySubmission } from '../services/picksApi';

import Card from './ui/Card';

const STATUS_CLASS: Record<MySubmission['submissionStatus'], string> = {
  PENDING: 'bg-yellow-50 text-yellow-800',
  APPROVED: 'bg-primary/10 text-primary',
  MERGED: 'bg-primary/10 text-primary',
  REJECTED: 'bg-red-50 text-red-700',
};

/**
 * "我的推荐" in My Home (PRD §6.3, §8.4): start a suggestion, and follow each one:
 * waiting, published, merged into an existing pick, or not approved with the reason.
 */
export default function MySubmissionsTab() {
  const { t, i18n } = useTranslation(['picks', 'dashboard']);
  const [items, setItems] = useState<MySubmission[] | null>(null);
  const [failed, setFailed] = useState(false);
  const locale = i18n.language;

  useEffect(() => {
    let cancelled = false;
    picksApi
      .mySubmissions()
      .then((list) => !cancelled && setItems(list))
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
    };
  }, []);

  const start = (
    <Link
      to="/picks/submit"
      className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary-dark"
    >
      <Plus className="h-4 w-4" aria-hidden="true" />
      {t('picks:list.suggest')}
    </Link>
  );

  if (failed) {
    return (
      <Card padding="lg">
        <p className="py-8 text-center text-gray-600">{t('picks:mine.loadError')}</p>
      </Card>
    );
  }
  if (!items) {
    return (
      <Card padding="lg">
        <div className="py-8 text-center">
          <Loader2 className="mx-auto h-8 w-8 animate-spin text-primary" />
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-gray-600">{t('picks:mine.intro')}</p>
        {start}
      </div>
      {items.length === 0 ? (
        <Card padding="lg">
          <p className="py-6 text-center text-gray-600">{t('picks:mine.empty')}</p>
        </Card>
      ) : (
        <ul className="space-y-3">
          {items.map((s) => {
            const title = localize(s.titleCN, s.titleEN, locale) || s.providerName;
            return (
              <li key={s.id} className="rounded-lg border border-gray-200 bg-white p-4">
                <div className="flex items-center justify-between gap-3">
                  {s.submissionStatus === 'APPROVED' && s.status === 'PUBLISHED' ? (
                    <Link
                      to={`/picks/${s.slug}`}
                      className="min-w-0 truncate text-sm font-semibold text-gray-900 hover:text-primary"
                    >
                      {title}
                    </Link>
                  ) : (
                    <span className="min-w-0 truncate text-sm font-semibold text-gray-900">
                      {title}
                    </span>
                  )}
                  <span
                    className={`flex-shrink-0 rounded-full px-2 py-0.5 text-xs ${STATUS_CLASS[s.submissionStatus]}`}
                  >
                    {t(`picks:mine.status.${s.submissionStatus}`)}
                  </span>
                </div>
                {s.submissionStatus === 'MERGED' && s.mergedInto && (
                  <p className="mt-2 text-sm text-gray-600">
                    {t('picks:mine.mergedInto')}{' '}
                    <Link
                      to={`/picks/${s.mergedInto.slug}`}
                      className="font-medium text-primary hover:underline"
                    >
                      {localize(s.mergedInto.titleCN, s.mergedInto.titleEN, locale)}
                    </Link>
                  </p>
                )}
                {s.submissionStatus === 'REJECTED' && s.rejectionReason && (
                  <p className="mt-2 text-xs text-red-700">
                    {t('picks:mine.rejected', { reason: s.rejectionReason })}
                  </p>
                )}
                <p className="mt-2 text-xs text-gray-400">
                  {new Date(s.createdAt).toLocaleDateString(locale, {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </p>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
